import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  AccessTime,
  Close,
  Edit,
  EditLocation,
  LocalHospital,
  Map,
  Person,
  Phone,
  Publish,
  WarningAmber,
} from '@mui/icons-material'
import moment from 'moment'

import API from '../../../../services/API'
import { GlobalContext, UserContext } from '../../../../App'
import { ControlRoomContext } from './ControlPage'
import { calculateSuggestedPriority } from '../../../../helpers/callTriage'

import AssignedResources from './SelectedIncident/AssignedResources'
import IncidentLogs from './SelectedIncident/IncidentLogs'
import IncidentLocationPicker, {
  parseCoordinatePair,
} from './IncidentLocationPicker'

// Coordinates are stored lng-first (map/GeoJSON order); people read lat-first.
const displayCoordinates = (value) => {
  const parsed = parseCoordinatePair(value)
  if (!parsed) return value
  return `${parsed[1].toFixed(5)}, ${parsed[0].toFixed(5)}`
}

export const getDisplayIncidentNumber = (incidentNumber) => {
  const str = String(incidentNumber ?? '')

  if (str.length > 4) {
    return `${str.slice(0, -4)}-${str.slice(-4)}`
  }

  return str
}

const statusConfig = {
  New: { color: '#D7263D', bg: 'rgba(215,38,61,0.12)' },
  Active: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  Closed: { color: '#0E9F6E', bg: 'rgba(14,159,110,0.12)' },
  Cancelled: { color: '#64748B', bg: 'rgba(100,116,139,0.12)' },
}

const terminalStatuses = ['Closed', 'Cancelled']

const priorityConfig = {
  Low: { color: '#16A34A', bg: 'rgba(22,163,74,0.12)' },
  Moderate: { color: '#2563EB', bg: 'rgba(37,99,235,0.12)' },
  High: { color: '#EA580C', bg: 'rgba(234,88,12,0.12)' },
  Critical: { color: '#DC2626', bg: 'rgba(220,38,38,0.12)' },
}

const incidentTypes = [
  'Medical',
  'Transfer',
  'Patient Transport',
  'Fire',
  'Security',
  'Crowd',
  'Logistical',
  'Other',
]

const callerRoleOptions = [
  'First Party Caller',
  'Third Party Caller',
  'Police',
  'Fire',
  'NHS Ambulance Service',
  'Running Call',
  'Self Presenting',
  'Event Control',
  'Steward/Security',
  'Medical Team',
  'Venue Staff',
  'Public',
]

const normalizeIncident = (incident) => {
  if (!incident) return incident

  return {
    ...incident,
    id: incident.id ?? incident._id,
    status: incident.status ?? incident.incidentStatus,
    type: incident.type ?? incident.incidentType,
    assignedResources: (incident.assignedResources ?? []).map((assigned) => ({
      ...assigned,
      resourceId:
        assigned.resourceId ??
        (assigned.resource?._id ? String(assigned.resource._id) : null),
      callSign: assigned.callSign ?? assigned.resource?.callSign,
      currentStatus: assigned.currentStatus ?? assigned.resource?.status ?? '',
    })),
  }
}

const normaliseEventMapCoordinates = (value) =>
  Array.isArray(value) && value.length === 2
    ? value.map((coordinate) => Number(coordinate))
    : null

const eventMapCoordinatesEqual = (left, right) => {
  const normalLeft = normaliseEventMapCoordinates(left)
  const normalRight = normaliseEventMapCoordinates(right)

  if (!normalLeft && !normalRight) return true
  if (!normalLeft || !normalRight) return false

  return normalLeft[0] === normalRight[0] && normalLeft[1] === normalRight[1]
}

const normaliseSearch = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const complaintSearchText = (complaint) =>
  normaliseSearch(
    [
      complaint?.code,
      complaint?.category,
      complaint?.presentingComplaint,
      complaint?.description,
      ...(complaint?.keywords ?? []),
    ]
      .filter(Boolean)
      .join(' '),
  )

const filterTriageComplaints = (options, { inputValue }) => {
  const terms = normaliseSearch(inputValue).split(' ').filter(Boolean)
  if (!terms.length) return options

  return options.filter((option) => {
    const searchText = complaintSearchText(option)
    return terms.every((term) => searchText.includes(term))
  })
}

const triageEditFieldSx = {
  '& .MuiInputLabel-root': {
    backgroundColor: 'white',
    px: 0.75,
    borderRadius: 0.75,
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
  },
}

const ruleMatchesAnswer = (rule, answer) => {
  const operator = rule?.when?.operator
  const expected = rule?.when?.value

  if (operator === 'equals') return answer === expected
  if (operator === 'notEquals') return answer !== expected
  if (operator === 'exists')
    return answer !== undefined && answer !== null && answer !== ''
  if (operator === 'notExists')
    return answer === undefined || answer === null || answer === ''

  return false
}

const answerTone = (question, answer) => {
  const matchingRules = (question.rules ?? []).filter((rule) =>
    ruleMatchesAnswer(rule, answer),
  )

  if (
    matchingRules.some((rule) =>
      ['setPriority', 'minimumPriority', 'increasePriority'].includes(
        rule.action?.type,
      ),
    )
  ) {
    return {
      backgroundColor: 'rgba(220,38,38,0.14)',
      color: '#991B1B',
      borderColor: 'rgba(220,38,38,0.5) !important',
    }
  }

  if (matchingRules.some((rule) => rule.action?.type === 'decreasePriority')) {
    return {
      backgroundColor: 'rgba(22,163,74,0.16)',
      color: '#166534',
      borderColor: 'rgba(22,163,74,0.55) !important',
    }
  }

  if (matchingRules.length) {
    return {
      backgroundColor: 'rgba(245,158,11,0.16)',
      color: '#92400E',
      borderColor: 'rgba(245,158,11,0.55) !important',
    }
  }

  return {
    backgroundColor: 'rgba(37,99,235,0.12)',
    color: '#1D4ED8',
    borderColor: 'rgba(37,99,235,0.35) !important',
  }
}

const TriageEditQuestion = ({ question, value, onChange }) => {
  const questionId = String(question._id)

  if (question.type !== 'yesNo') {
    return (
      <TextField
        label={question.text}
        value={value ?? ''}
        fullWidth
        select={question.type === 'singleSelect'}
        type={question.type === 'number' ? 'number' : 'text'}
        onChange={(e) =>
          onChange(
            questionId,
            question.type === 'number'
              ? Number(e.target.value)
              : e.target.value,
          )
        }
      >
        {(question.options ?? []).map((option) => (
          <MenuItem key={option.label} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  const selectedSx = answerTone(question, value)

  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
      <Stack spacing={1}>
        <Typography fontWeight={800}>{question.text}</Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={value ?? null}
          onChange={(_, nextValue) =>
            onChange(questionId, nextValue ?? undefined)
          }
          sx={{
            gap: 1,
            '& .MuiToggleButtonGroup-grouped': {
              border: '1px solid rgba(15,23,42,0.14) !important',
              borderRadius: '12px !important',
              minHeight: 44,
              fontWeight: 900,
              textTransform: 'none',
            },
          }}
        >
          {['Yes', 'No'].map((answer) => (
            <ToggleButton
              key={answer}
              value={answer}
              sx={{
                '&.Mui-selected': selectedSx,
                '&.Mui-selected:hover': selectedSx,
              }}
            >
              {answer}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
    </Paper>
  )
}

const ElapsedTime = React.memo(({ createdAt, status }) => {
  const [elapsedTime, setElapsedTime] = useState('')
  const isClosed = terminalStatuses.includes(status)

  useEffect(() => {
    if (isClosed) {
      setElapsedTime('Incident closed')
      return
    }

    if (!createdAt) return

    const updateElapsed = () => {
      const duration = moment.duration(moment().diff(createdAt))

      setElapsedTime(
        `${duration.days()}d ${duration.hours()}h ${duration.minutes()}m ${duration.seconds()}s`,
      )
    }

    updateElapsed()

    const interval = setInterval(updateElapsed, 1000)

    return () => clearInterval(interval)
  }, [createdAt, isClosed])

  return (
    <Typography component="span" variant="inherit" fontWeight={700}>
      {elapsedTime}
    </Typography>
  )
})

const CallTriageSummary = ({ callTriage, canEdit, onEdit }) => {
  if (!callTriage) return null

  const answeredQuestions = (callTriage.answers ?? []).filter(
    (answer) =>
      answer?.answer !== undefined &&
      answer?.answer !== null &&
      answer?.answer !== '',
  )
  const ruleToneByQuestionId = (callTriage.triggeredRules ?? []).reduce(
    (acc, rule) => {
      const questionId = String(rule?.questionId ?? '')
      const actionType = rule?.action?.type
      if (!questionId) return acc

      if (
        ['setPriority', 'minimumPriority', 'increasePriority'].includes(
          actionType,
        )
      ) {
        acc[questionId] = 'error'
        return acc
      }

      if (actionType === 'decreasePriority') {
        acc[questionId] = 'success'
        return acc
      }

      if (!acc[questionId]) acc[questionId] = 'warning'
      return acc
    },
    {},
  )

  return (
    <Grid item xs={12}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 4,
          border: '1px solid rgba(35,154,164,0.24)',
          backgroundColor: 'rgba(35,154,164,0.04)',
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            gap={1}
          >
            <Stack spacing={0.25}>
              <Typography fontWeight={900} color="#242F40">
                Call Triage
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {callTriage.category} · {callTriage.presentingComplaint}
              </Typography>
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip
                label={`Suggested Priority: ${
                  callTriage.calculatedSuggestedPriority ||
                  callTriage.defaultPriority ||
                  'Not recorded'
                }`}
                color="primary"
              />
              <Chip
                label={`Final Priority: ${
                  callTriage.finalPriority || 'Not recorded'
                }`}
                sx={{
                  fontWeight: 800,
                  backgroundColor:
                    priorityConfig[callTriage.finalPriority]?.bg ??
                    'rgba(100,116,139,0.12)',
                  color:
                    priorityConfig[callTriage.finalPriority]?.color ??
                    '#475569',
                }}
              />
              {callTriage.overridden && (
                <Chip color="warning" label="Overridden" />
              )}
              {canEdit && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={onEdit}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 800,
                  }}
                >
                  Edit answers
                </Button>
              )}
            </Stack>
          </Stack>

          {callTriage.overridden && callTriage.overrideReason && (
            <Alert severity="warning" variant="outlined">
              Override Reason: {callTriage.overrideReason}
            </Alert>
          )}

          {!!callTriage.warnings?.length && (
            <Alert severity="warning" variant="outlined">
              {callTriage.warnings.join(' ')}
            </Alert>
          )}
          <Grid container spacing={1}>
            {answeredQuestions.map((answer) => (
              <Grid item xs={12} md={6} lg={4} key={answer.questionId}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    backgroundColor: 'white',
                    // height: '100%',
                  }}
                >
                  <Stack
                    spacing={0.75}
                    direction={{ xs: 'column', lg: 'row' }}
                    justifyContent="space-between"
                  >
                    <Typography variant="body2" fontWeight={800}>
                      {answer.questionText}
                    </Typography>
                    <Chip
                      size="small"
                      label={String(answer.answer)}
                      color={
                        ruleToneByQuestionId[String(answer.questionId)] ??
                        'default'
                      }
                      sx={{ alignSelf: 'flex-start', fontWeight: 800 }}
                    />
                  </Stack>
                </Paper>
              </Grid>
            ))}
            {!answeredQuestions.length && (
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined">
                  No triage answers were recorded for this incident.
                </Alert>
              </Grid>
            )}
          </Grid>
        </Stack>
      </Paper>
    </Grid>
  )
}

const CallTriageEditPanel = ({
  open,
  complaints,
  complaint,
  onComplaintChange,
  answers,
  onAnswerChange,
  preview,
  finalPriority,
  onFinalPriorityChange,
  overrideReason,
  onOverrideReasonChange,
  onCancel,
  onSave,
  saving,
}) => {
  if (!open) return null

  if (!complaint) {
    return (
      <Grid item xs={12}>
        <Alert severity="warning" variant="outlined">
          The approved Call Triage configuration for this incident could not be
          loaded, so the answers cannot be edited here.
        </Alert>
      </Grid>
    )
  }

  const calculatedPriority =
    preview?.calculatedPriority ?? complaint.defaultPriority
  const effectiveFinalPriority = finalPriority || calculatedPriority
  const overrideRequired = effectiveFinalPriority !== calculatedPriority

  return (
    <Grid item xs={12}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 4,
          border: '1px solid rgba(37,99,235,0.22)',
          backgroundColor: 'rgba(37,99,235,0.04)',
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            gap={1}
          >
            <Box>
              <Typography fontWeight={900} color="#242F40">
                Edit Call Triage Answers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {complaint.category} · {complaint.presentingComplaint}
              </Typography>
            </Box>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip
                label={`Suggested Priority: ${calculatedPriority}`}
                color="primary"
                sx={{ fontWeight: 800 }}
              />
              <Chip
                label={`Default: ${preview?.defaultPriority ?? complaint.defaultPriority}`}
                sx={{ fontWeight: 800 }}
              />
            </Stack>
          </Stack>

          <Autocomplete
            options={complaints}
            filterOptions={filterTriageComplaints}
            value={complaint}
            disableClearable
            onChange={(_, value) => onComplaintChange(value)}
            groupBy={(option) => option.category}
            getOptionLabel={(option) =>
              option
                ? `${option.presentingComplaint} (${option.defaultPriority})`
                : ''
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Presenting complaint"
                helperText="Only approved and enabled call triage complaints are shown."
                sx={triageEditFieldSx}
              />
            )}
          />

          <Grid container spacing={1.5}>
            {(complaint.questions ?? []).map((question) => (
              <Grid item xs={12} md={6} lg={4} key={question._id}>
                <TriageEditQuestion
                  question={question}
                  value={answers[String(question._id)]}
                  onChange={onAnswerChange}
                />
              </Grid>
            ))}
          </Grid>

          {!!preview?.warnings?.length && (
            <Alert severity="warning" variant="outlined">
              {preview.warnings.join(' ')}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Final Priority"
                value={effectiveFinalPriority}
                onChange={(e) => onFinalPriorityChange(e.target.value)}
                select
                fullWidth
                sx={triageEditFieldSx}
              >
                {['Low', 'Moderate', 'High', 'Critical'].map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {priority}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {overrideRequired && (
              <Grid item xs={12} md={8}>
                <TextField
                  label="Override Reason"
                  value={overrideReason}
                  onChange={(e) => onOverrideReasonChange(e.target.value)}
                  fullWidth
                  required
                  sx={triageEditFieldSx}
                />
              </Grid>
            )}
          </Grid>

          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              variant="contained"
              onClick={onSave}
              disabled={saving || (overrideRequired && !overrideReason.trim())}
              sx={{ textTransform: 'none', fontWeight: 900 }}
            >
              {saving ? 'Saving...' : 'Save triage answers'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Grid>
  )
}

const SelectedIncident = ({ data }) => {
  const { setToast } = useContext(GlobalContext)
  const { user } = useContext(UserContext)

  const {
    selectedIncident,
    setSelectedIncident,
    selectedControlRoom,
    eventMap,
    mapView,
    canDispatch = true,
  } = useContext(ControlRoomContext)

  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null)
  const [typeMenuAnchor, setTypeMenuAnchor] = useState(null)
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState(null)

  const [description, setDescription] = useState('')
  const [presentingComplaint, setPresentingComplaint] = useState('')
  const [location, setLocation] = useState('')
  const [what3words, setWhat3words] = useState('')
  const [coordinates, setCoordinates] = useState('')
  const [eventMapCoordinates, setEventMapCoordinates] = useState(null)
  const [locationEditingUnlocked, setLocationEditingUnlocked] = useState(false)
  const [openIncidentMap, setOpenIncidentMap] = useState(false)

  const [callerName, setCallerName] = useState('')
  const [callerPhone, setCallerPhone] = useState('')
  const [callerRole, setCallerRole] = useState('First Party Caller')
  const [triageEditOpen, setTriageEditOpen] = useState(false)
  const [triageEditComplaintId, setTriageEditComplaintId] = useState('')
  const [triageEditAnswers, setTriageEditAnswers] = useState({})
  const [triageEditFinalPriority, setTriageEditFinalPriority] = useState('')
  const [triageEditOverrideReason, setTriageEditOverrideReason] = useState('')
  const [approvedTriageComplaints, setApprovedTriageComplaints] = useState([])
  const [savingTriageEdit, setSavingTriageEdit] = useState(false)

  const incidentID = selectedIncident?.id

  const statusStyle = statusConfig[selectedIncident?.status] || statusConfig.New

  const priorityStyle =
    priorityConfig[selectedIncident?.priority] || priorityConfig.Moderate

  const createdById =
    selectedIncident?.createdBy?._id ?? selectedIncident?.createdBy ?? null
  const currentUserId = user?._id ?? user?.id
  const isCommander =
    user?.employee?.cadAccess === 'Commander' || !!user?.security?.isSiteAdmin
  const canEditCallTriage =
    canDispatch &&
    !!selectedIncident?.callTriage &&
    (String(createdById) === String(currentUserId) || isCommander)

  const triageEditComplaint = useMemo(() => {
    const complaintId = String(
      triageEditComplaintId || selectedIncident?.callTriage?.complaintId || '',
    )
    const complaintCode = selectedIncident?.callTriage?.complaintCode
    return approvedTriageComplaints.find(
      (complaint) =>
        String(complaint._id) === complaintId ||
        (!triageEditComplaintId && complaint.code === complaintCode),
    )
  }, [
    approvedTriageComplaints,
    selectedIncident?.callTriage,
    triageEditComplaintId,
  ])

  const triageEditPreview = useMemo(() => {
    if (!triageEditComplaint) return null
    return calculateSuggestedPriority({
      defaultPriority: triageEditComplaint.defaultPriority,
      questions: triageEditComplaint.questions,
      answers: triageEditAnswers,
    })
  }, [triageEditComplaint, triageEditAnswers])

  useEffect(() => {
    if (!canEditCallTriage || approvedTriageComplaints.length) return

    API.get('/configuration/command-control/call-triage/approved').then(
      (res) => {
        if (res.status === 200) setApprovedTriageComplaints(res.data ?? [])
      },
    )
  }, [canEditCallTriage, approvedTriageComplaints.length])

  useEffect(() => {
    if (!selectedIncident?.id || !data?.activeIncidents?.length) return

    const freshIncident = data.activeIncidents.find(
      (x) => x.id === selectedIncident.id,
    )

    if (!freshIncident) return

    setSelectedIncident((prev) => {
      if (!prev) return normalizeIncident(freshIncident)

      const prevUpdated = prev.updatedAt
        ? new Date(prev.updatedAt).getTime()
        : 0

      const freshUpdated = freshIncident.updatedAt
        ? new Date(freshIncident.updatedAt).getTime()
        : 0

      if (freshUpdated <= prevUpdated) return prev

      return {
        ...prev,
        ...normalizeIncident(freshIncident),
      }
    })
  }, [
    data?.version,
    selectedIncident?.id,
    data?.activeIncidents,
    setSelectedIncident,
  ])

  useEffect(() => {
    if (!selectedIncident?.id) return

    if (!selectedIncident.location) {
      API.get(`/commandcontrol/fetchincidentdata/${selectedIncident.id}`).then(
        (res) => {
          if (res.status === 200) {
            setSelectedIncident(normalizeIncident(res.data))
          }
        },
      )
    }
  }, [selectedIncident?.id, selectedIncident?.location, setSelectedIncident])

  useEffect(() => {
    setLocation(selectedIncident?.location ?? '')
    setWhat3words(selectedIncident?.what3words ?? '')
    setCoordinates(selectedIncident?.coordinates ?? '')
    setEventMapCoordinates(selectedIncident?.eventMapCoordinates ?? null)
    setLocationEditingUnlocked(false)
    setOpenIncidentMap(false)
    setDescription(selectedIncident?.description ?? '')
    setPresentingComplaint(selectedIncident?.presentingComplaint ?? '')
    setCallerName(selectedIncident?.caller?.name ?? '')
    setCallerPhone(selectedIncident?.caller?.phone ?? '')
    setCallerRole(selectedIncident?.caller?.role ?? 'First Party Caller')
    setTriageEditOpen(false)
    setTriageEditComplaintId(
      selectedIncident?.callTriage?.complaintId
        ? String(selectedIncident.callTriage.complaintId)
        : '',
    )
    setTriageEditAnswers(
      (selectedIncident?.callTriage?.answers ?? []).reduce((acc, answer) => {
        acc[String(answer.questionId)] = answer.answer
        return acc
      }, {}),
    )
    setTriageEditFinalPriority(
      selectedIncident?.callTriage?.finalPriority ??
        selectedIncident?.callTriage?.calculatedSuggestedPriority ??
        '',
    )
    setTriageEditOverrideReason(
      selectedIncident?.callTriage?.overrideReason ?? '',
    )
  }, [selectedIncident?.id])

  useEffect(() => {
    if (!triageEditPreview || triageEditOpen) return
    setTriageEditFinalPriority(triageEditPreview.calculatedPriority)
  }, [triageEditPreview?.calculatedPriority, triageEditOpen])

  const saveTriageEdit = async () => {
    if (
      !selectedIncident?.id ||
      !selectedIncident?.callTriage ||
      !triageEditComplaint
    ) {
      return
    }

    try {
      setSavingTriageEdit(true)
      const res = await API.post(
        `/commandcontrol/incident/${selectedIncident.id}/call-triage`,
        {
          callTriage: {
            complaintId: triageEditComplaint._id,
            answers: triageEditAnswers,
            finalPriority:
              triageEditFinalPriority ||
              triageEditPreview?.calculatedPriority ||
              selectedIncident.callTriage.finalPriority,
            overrideReason: triageEditOverrideReason,
          },
        },
      )

      if (res.status === 200) {
        setToast({ message: 'Call Triage updated' })
        setSelectedIncident(normalizeIncident(res.data))
        setTriageEditOpen(false)
        return
      }

      setToast({
        message: res.data?.error || 'Unable to update Call Triage',
        severity: 'error',
      })
    } catch (err) {
      setToast({
        message: 'Unable to update Call Triage',
        severity: 'error',
      })
    } finally {
      setSavingTriageEdit(false)
    }
  }

  const buildPendingChanges = () => {
    if (!selectedIncident) return null

    const changes = {}

    if (description !== (selectedIncident.description ?? '')) {
      changes.description = description
    }

    if (
      !selectedIncident.callTriage &&
      presentingComplaint !== (selectedIncident.presentingComplaint ?? '')
    ) {
      changes.presentingComplaint = presentingComplaint
    }

    if (location !== (selectedIncident.location ?? '')) {
      changes.location = location
    }

    if (what3words !== (selectedIncident.what3words ?? '')) {
      changes.what3words = what3words
    }

    if (coordinates !== (selectedIncident.coordinates ?? '')) {
      changes.coordinates = coordinates
    }

    if (
      !eventMapCoordinatesEqual(
        eventMapCoordinates,
        selectedIncident.eventMapCoordinates,
      )
    ) {
      changes.eventMapCoordinates = eventMapCoordinates
    }

    const callerChanged =
      callerName !== (selectedIncident.caller?.name ?? '') ||
      callerPhone !== (selectedIncident.caller?.phone ?? '') ||
      callerRole !== (selectedIncident.caller?.role ?? 'First Party Caller')

    if (callerChanged) {
      changes.caller = {
        ...(selectedIncident.caller ?? {}),
        name: callerName,
        phone: callerPhone,
        role: callerRole,
      }
    }

    return Object.keys(changes).length ? changes : null
  }

  const handleChange = async (changes, customLogNote) => {
    // View-only roles never mutate — every edit path in this modal funnels
    // through here, so this backstops the disabled controls.
    if (!canDispatch) return false
    if (!incidentID || !selectedControlRoom || !changes) return false

    try {
      const res = await API.post('/commandcontrol/incidentupdate', {
        ...changes,
        logNote: customLogNote,
        controlRoom: selectedControlRoom,
        incidentID,
      })

      if (res.status === 200) {
        setToast({ message: 'Incident Updated' })

        setStatusMenuAnchor(null)
        setTypeMenuAnchor(null)
        setPriorityMenuAnchor(null)

        setSelectedIncident((prev) => {
          if (!prev || prev.id !== incidentID) return prev

          return {
            ...prev,
            status: changes?.incidentStatus ?? prev?.status,
            type: changes?.incidentType ?? prev?.type,
            ...changes,
          }
        })

        return true
      }
    } catch (err) {
      setToast({
        message: 'Unable to update incident',
        severity: 'error',
      })
    }

    return false
  }

  const savePendingChanges = async () => {
    const changes = buildPendingChanges()

    if (!changes) return true

    const logNotes = []

    if (changes.description) {
      logNotes.push(`Description updated to: ${changes.description}`)
    }

    if (changes.presentingComplaint) {
      logNotes.push(
        `Presenting complaint updated to: ${changes.presentingComplaint}`,
      )
    }

    if (changes.location) {
      logNotes.push(`Location updated to: ${changes.location}`)
    }

    if (changes.what3words) {
      logNotes.push(`What3Words updated to: ${changes.what3words}`)
    }

    if (
      Object.prototype.hasOwnProperty.call(changes, 'coordinates') ||
      Object.prototype.hasOwnProperty.call(changes, 'eventMapCoordinates')
    ) {
      logNotes.push('Incident map coordinates updated')
    }

    if (changes.caller) {
      logNotes.push('Caller information updated')
    }

    return handleChange(changes, logNotes.join('. '))
  }

  // Drives the "Push update" header button: edits sit locally until the
  // dispatcher pushes them (or closes the drawer, which saves too)
  const hasPendingChanges = useMemo(
    () => !!buildPendingChanges(),
    [
      description,
      presentingComplaint,
      location,
      what3words,
      coordinates,
      eventMapCoordinates,
      callerName,
      callerPhone,
      callerRole,
      selectedIncident,
    ],
  )

  const handleClose = async () => {
    await savePendingChanges()
    setSelectedIncident(null)
  }

  const handleIncidentMapClose = async () => {
    const saved = await savePendingChanges()
    if (saved) {
      setOpenIncidentMap(false)
    }
  }

  const handleStatusChange = (incidentStatus, customLogNote) => {
    handleChange(
      { incidentStatus },
      customLogNote ?? `Status changed to ${incidentStatus}`,
    )
  }

  const handleTypeChange = (incidentType) => {
    handleChange({ incidentType }, `Type changed to ${incidentType}`)
  }

  const handlePriorityChange = (priority) => {
    handleChange({ priority }, `Priority changed to ${priority}`)
  }

  const hasAssignedResources = useMemo(() => {
    return (selectedIncident?.assignedResources ?? []).some(
      (x) => !x?.times?.clear,
    )
  }, [selectedIncident])

  const assignedResourceCount = useMemo(
    () =>
      (selectedIncident?.assignedResources ?? []).filter(
        (x) => !x?.times?.clear,
      ).length,
    [selectedIncident?.assignedResources],
  )

  const isTerminalIncident = terminalStatuses.includes(selectedIncident?.status)

  return (
    <Drawer
      anchor="bottom"
      open={!!selectedIncident}
      onClose={handleClose}
      PaperProps={{
        sx: {
          height: '88vh',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #F8FAFC 0%, #EEF2F7 100%)',
        },
      }}
    >
      <Stack sx={{ height: '100%', minHeight: 0 }}>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2,
            background:
              'linear-gradient(135deg, #182230 0%, #243447 65%, #2563EB 180%)',
            color: 'white',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {/* Phones: title + close share the first row; status/chips follow.
              Desktop (lg) keeps the original title → status → chips → close order. */}
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={8} lg={3} sx={{ order: { xs: 1, lg: 1 } }}>
              <Typography
                sx={{
                  fontSize: '1.22rem',
                  fontWeight: 900,
                  letterSpacing: 0.3,
                  color: 'white',
                }}
              >
                Incident{' '}
                {getDisplayIncidentNumber(
                  selectedIncident?.incidentNumber ?? 0,
                )}
              </Typography>
            </Grid>

            <Grid item xs={12} lg={3} sx={{ order: { xs: 3, lg: 2 } }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!canDispatch}
                  onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                  sx={{
                    borderRadius: 999,
                    px: 2,
                    fontWeight: 800,
                    backgroundColor: statusStyle.color,
                    '&.Mui-disabled': {
                      backgroundColor: statusStyle.color,
                      color: 'white',
                      opacity: 0.85,
                    },
                  }}
                >
                  {selectedIncident?.status || selectedIncident?.incidentStatus}
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  disabled={!canDispatch}
                  onClick={(e) => setTypeMenuAnchor(e.currentTarget)}
                  sx={{
                    borderRadius: 999,
                    px: 2,
                    fontWeight: 800,
                    backgroundColor: '#2563EB',
                    '&.Mui-disabled': {
                      backgroundColor: '#2563EB',
                      color: 'white',
                      opacity: 0.85,
                    },
                  }}
                >
                  {selectedIncident?.type || selectedIncident?.incidentType}
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  disabled={!canDispatch}
                  onClick={(e) => setPriorityMenuAnchor(e.currentTarget)}
                  sx={{
                    borderRadius: 999,
                    px: 2,
                    fontWeight: 800,
                    backgroundColor: priorityStyle.color,
                    '&.Mui-disabled': {
                      backgroundColor: priorityStyle.color,
                      color: 'white',
                      opacity: 0.85,
                    },
                  }}
                >
                  {selectedIncident?.priority}
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={4} sx={{ order: { xs: 4, lg: 3 } }}>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
              >
                <Chip
                  icon={<AccessTime />}
                  label={moment(selectedIncident?.createdAt).format(
                    'DD/MM/YYYY HH:mm',
                  )}
                  size="small"
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    fontWeight: 800,
                    '& .MuiChip-icon': { color: 'white' },
                  }}
                />
                <Chip
                  icon={<WarningAmber />}
                  label={
                    <ElapsedTime
                      createdAt={selectedIncident?.createdAt}
                      status={selectedIncident?.status}
                    />
                  }
                  size="small"
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    fontWeight: 800,
                    '& .MuiChip-icon': { color: 'white' },
                  }}
                />
                <Chip
                  icon={<LocalHospital />}
                  label={`${assignedResourceCount} assigned`}
                  size="small"
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    fontWeight: 800,
                    '& .MuiChip-icon': { color: 'white' },
                  }}
                />
              </Stack>
            </Grid>

            <Grid item xs={4} lg={2} sx={{ order: { xs: 2, lg: 4 } }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="flex-end"
                flexWrap="wrap"
                useFlexGap
              >
                {hasPendingChanges && (
                  <Button
                    variant="contained"
                    startIcon={<Publish />}
                    onClick={() => savePendingChanges()}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 800,
                      whiteSpace: 'nowrap',
                      backgroundColor: '#239AA4',
                      '&:hover': { backgroundColor: '#1C7B83' },
                      animation: 'pushUpdatePulse 2s ease-out infinite',
                      '@keyframes pushUpdatePulse': {
                        '0%': {
                          boxShadow: '0 0 0 0 rgba(35,154,164,0.65)',
                        },
                        '70%': {
                          boxShadow: '0 0 0 12px rgba(35,154,164,0)',
                        },
                        '100%': {
                          boxShadow: '0 0 0 0 rgba(35,154,164,0)',
                        },
                      },
                    }}
                  >
                    Push update
                  </Button>
                )}
                <Tooltip title="Close">
                  <IconButton
                    onClick={handleClose}
                    sx={{
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)',
                    }}
                  >
                    <Close />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            px: { xs: 1.5, md: 3 },
            py: { xs: 2, md: 3 },
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 4,
                  border: '1px solid rgba(15,23,42,0.08)',
                  flex: 1,
                }}
              >
                <Stack spacing={2}>
                  <Typography fontWeight={800}>Incident Summary</Typography>

                  {selectedIncident?.callTriage ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: 'rgba(35,154,164,0.04)',
                        borderColor: 'rgba(35,154,164,0.22)',
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Selected Presenting Complaint
                        </Typography>
                        <Typography fontWeight={900} color="#242F40">
                          {selectedIncident.callTriage.presentingComplaint ||
                            selectedIncident.presentingComplaint ||
                            'Not recorded'}
                        </Typography>
                        {selectedIncident.callTriage.category && (
                          <Typography variant="body2" color="text.secondary">
                            {selectedIncident.callTriage.category}
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  ) : (
                    <TextField
                      label="Presenting Complaint"
                      value={presentingComplaint}
                      onChange={(e) => setPresentingComplaint(e.target.value)}
                      fullWidth
                    />
                  )}

                  <TextField
                    label="Summary"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 4,
                  border: '1px solid rgba(15,23,42,0.08)',
                  flex: 1,
                }}
              >
                <Stack spacing={2}>
                  <Typography fontWeight={800}>Incident Location</Typography>

                  <TextField
                    label="Location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    fullWidth
                    multiline
                  />

                  <TextField
                    label="What3Words"
                    value={what3words}
                    onChange={(e) => setWhat3words(e.target.value)}
                    fullWidth
                    multiline
                  />
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 4,
                  border: '1px solid rgba(15,23,42,0.08)',
                  flex: 1,
                }}
              >
                <Stack spacing={1}>
                  <Typography fontWeight={900} color="#242F40">
                    Caller Information
                  </Typography>
                  <Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Caller name"
                          fullWidth
                          value={callerName}
                          onChange={(e) => setCallerName(e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 3,
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Phone number"
                          fullWidth
                          value={callerPhone}
                          onChange={(e) => setCallerPhone(e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 3,
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Autocomplete
                          freeSolo
                          value={callerRole}
                          options={callerRoleOptions}
                          onChange={(e, newValue) =>
                            setCallerRole(newValue || '')
                          }
                          onInputChange={(e, newInputValue) =>
                            setCallerRole(newInputValue)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Caller Role"
                              helperText="Type a custom value and press Enter"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 3,
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <CallTriageSummary
              callTriage={selectedIncident?.callTriage}
              canEdit={canEditCallTriage}
              onEdit={() => setTriageEditOpen(true)}
            />

            <CallTriageEditPanel
              open={triageEditOpen}
              complaints={approvedTriageComplaints}
              complaint={triageEditComplaint}
              onComplaintChange={(complaint) => {
                setTriageEditComplaintId(
                  complaint?._id ? String(complaint._id) : '',
                )
                setTriageEditAnswers({})
                setTriageEditFinalPriority(complaint?.defaultPriority ?? '')
                setTriageEditOverrideReason('')
              }}
              answers={triageEditAnswers}
              onAnswerChange={(questionId, value) => {
                setTriageEditAnswers((prev) => ({
                  ...prev,
                  [questionId]: value,
                }))
                setTriageEditFinalPriority('')
              }}
              preview={triageEditPreview}
              finalPriority={triageEditFinalPriority}
              onFinalPriorityChange={setTriageEditFinalPriority}
              overrideReason={triageEditOverrideReason}
              onOverrideReasonChange={setTriageEditOverrideReason}
              onCancel={() => setTriageEditOpen(false)}
              onSave={saveTriageEdit}
              saving={savingTriageEdit}
            />

            <AssignedResources data={data} />

            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 4,
                  border: '1px solid rgba(15,23,42,0.08)',
                }}
              >
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                  >
                    <Stack spacing={0.5}>
                      <Typography fontWeight={800}>Incident Map</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {coordinates
                          ? `Map coordinates: ${displayCoordinates(coordinates)}`
                          : eventMapCoordinates?.length === 2
                            ? `Event map position: ${eventMapCoordinates
                                .map((value) => `${Math.round(value * 100)}%`)
                                .join(', ')}`
                            : 'No map position recorded'}
                      </Typography>
                    </Stack>

                    <Button
                      variant="contained"
                      startIcon={<Map />}
                      onClick={() => setOpenIncidentMap(true)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 900,
                        backgroundColor: '#239AA4',
                      }}
                    >
                      Open map
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            <IncidentLogs />
          </Grid>
        </Box>

        <Menu
          anchorEl={statusMenuAnchor}
          open={!!statusMenuAnchor}
          onClose={() => setStatusMenuAnchor(null)}
        >
          {isTerminalIncident && (
            <MenuItem
              onClick={() => handleStatusChange('Active', 'CAD reopened')}
            >
              Reopen CAD
            </MenuItem>
          )}

          {['New', 'Active'].map((x) => (
            <MenuItem key={x} onClick={() => handleStatusChange(x)}>
              {x}
            </MenuItem>
          ))}

          {['Cancelled', 'Closed'].map((x) => (
            <MenuItem
              key={x}
              disabled={hasAssignedResources}
              onClick={() => handleStatusChange(x)}
            >
              {x}
            </MenuItem>
          ))}
        </Menu>

        <Menu
          anchorEl={typeMenuAnchor}
          open={!!typeMenuAnchor}
          onClose={() => setTypeMenuAnchor(null)}
        >
          {incidentTypes.map((x) => (
            <MenuItem key={x} onClick={() => handleTypeChange(x)}>
              {x}
            </MenuItem>
          ))}
        </Menu>

        <Menu
          anchorEl={priorityMenuAnchor}
          open={!!priorityMenuAnchor}
          onClose={() => setPriorityMenuAnchor(null)}
        >
          {['Low', 'Moderate', 'High', 'Critical'].map((x) => (
            <MenuItem key={x} onClick={() => handlePriorityChange(x)}>
              {x}
            </MenuItem>
          ))}
        </Menu>

        <Dialog
          open={openIncidentMap}
          onClose={handleIncidentMapClose}
          fullWidth
          maxWidth="xl"
          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <EditLocation sx={{ color: '#239AA4' }} />
                <Box>
                  <Typography fontWeight={900}>Incident Map</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Incident{' '}
                    {getDisplayIncidentNumber(
                      selectedIncident?.incidentNumber ?? 0,
                    )}
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={handleIncidentMapClose}>
                <Close />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 2, backgroundColor: '#F8FAFC' }}>
            <IncidentLocationPicker
              coordinates={coordinates}
              setCoordinates={setCoordinates}
              eventMapCoordinates={eventMapCoordinates}
              setEventMapCoordinates={setEventMapCoordinates}
              eventMap={eventMap}
              initialMapView={mapView}
              height="min(72vh, 720px)"
              locked={!locationEditingUnlocked}
              onUnlock={() => setLocationEditingUnlocked(true)}
            />
          </DialogContent>
        </Dialog>
      </Stack>
    </Drawer>
  )
}

export default SelectedIncident
